import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/api";
import FadeImg from "@/components/FadeImg";
import { Reveal, MaskedLines, Overline, Magnetic } from "@/components/Motion";

const CATEGORIES = ["All", "Portraits", "Paintings", "Wall Art", "Illustration", "Commissioned Art", "Experimental Art"];

export default function Art() {
  const [artworks, setArtworks] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/artworks").then((r) => setArtworks(r.data)).catch(() => {});
  }, []);

  const list = cat === "All" ? artworks : artworks.filter((a) => a.category === cat);

  return (
    <main data-testid="art-page" className="pt-32 pb-24 min-h-screen">
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <Overline>Work — Art</Overline>
        <MaskedLines
          className="mt-6 mb-10"
          lineClassName="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight"
          lines={["Art gallery"]}
        />
        <Reveal className="mb-14">
          <div className="flex flex-wrap gap-2" data-testid="art-category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`art-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                  cat === c ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:balance]">
          {list.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08} className="break-inside-avoid mb-6">
              <Link to={`/work/art/${a.id}`} data-testid={`artwork-${a.id}`} className="group block">
                <div className="frame-clip spotlight bg-linen">
                  <FadeImg src={a.image} alt={a.title} className="w-full h-auto"
                    loading={i < 3 ? "eager" : "lazy"} fetchPriority={i < 3 ? "high" : undefined} />
                </div>
                <div className="mt-3 flex items-baseline justify-between gap-3">
                  <h2 className="font-display text-xl group-hover:text-brand transition-colors">{a.title}</h2>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 shrink-0">{a.year}</span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mt-1">
                  {a.category} · {a.medium}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
        {!list.length && <p className="text-ink/50 font-mono text-sm">No artworks in this category yet.</p>}
        <Reveal className="mt-20 bg-ink text-paper px-8 md:px-14 py-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">Commission an Artwork</h2>
            <p className="mt-3 text-paper/60 max-w-lg">Portraits, custom paintings, murals and installations — made to your brief, space and story.</p>
          </div>
          <Magnetic>
            <Link to="/contact?type=Artwork Commission" data-testid="commission-artwork-btn"
              className="inline-flex items-center gap-3 bg-brand text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors shrink-0">
              Commission an Artwork <ArrowRight size={14} />
            </Link>
          </Magnetic>
        </Reveal>
      </div>
    </main>
  );
}
